// Wall time with an explicit IANA zone, to a UTC instant (contract 3.4). The evidence routes take ISO 8601 instants, so the form that
// collects a time (a supplier's timestamp, the expiry of a quote) converts here, with the rules of the contract:
//   - The zone is always typed or chosen by the staff member. The browser zone is never read.
//   - A time that does not exist (a clock gap, for example 2026-03-29 01:30 in Europe/London) is refused. It is never moved.
//   - A time that happens twice (an overlap, for example 2026-10-25 01:30 in Europe/London) is refused until the staff member chooses
//     the earlier or the later one.
// The function uses `Intl.DateTimeFormat` only, no dependency. The server's own `zonedWallTimeToInstant` (domain `time-zone.ts`) is the
// reference; `wall-time.test.ts` checks this one against the contract's oracle values (3.4, point 11).

export type WallTimeInput = Readonly<{
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`, 24 hour. */
  time: string;
  /** An IANA zone name, for example `Europe/London`. */
  zone: string;
  /** Needed only for a time that happens twice. */
  choice?: "earlier" | "later" | undefined;
}>;

export type WallTimeFailure = "FORMAT" | "ZONE_UNKNOWN" | "NOT_EXISTENT" | "AMBIGUOUS";

export type WallTimeResult =
  | Readonly<{
      ok: true;
      /** `YYYY-MM-DDTHH:MM:SS.sssZ`. */
      instant: string;
      /** The UTC offset the conversion resolved, in minutes (+60 for UTC+01:00). */
      offsetMinutes: number;
      kind: "UNIQUE" | "OVERLAP_EARLIER" | "OVERLAP_LATER";
    }>
  | Readonly<{ ok: false; reason: WallTimeFailure }>;

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{2}):(\d{2})$/;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  const known = formatters.get(zone);
  if (known !== undefined) return known;
  const made = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
  formatters.set(zone, made);
  return made;
}

/** The text of a zone is valid when the runtime knows it. */
export function isKnownZone(zone: string): boolean {
  if (zone.trim() === "" || zone !== zone.trim()) return false;
  try {
    formatterFor(zone);
    return true;
  } catch {
    return false;
  }
}

/** The UTC offset of `zone` at `instantMs`, in minutes. */
function offsetMinutesAt(zone: string, instantMs: number): number {
  const parts = formatterFor(zone).formatToParts(new Date(instantMs));
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const wallAsUtc = Date.UTC(
    value("year"),
    value("month") - 1,
    value("day"),
    value("hour"),
    value("minute"),
    value("second"),
  );
  // Compare at whole seconds: the formatter drops the milliseconds.
  return Math.round((wallAsUtc - Math.floor(instantMs / 1000) * 1000) / 60000);
}

export function wallTimeToInstant(input: WallTimeInput): WallTimeResult {
  const date = DATE.exec(input.date);
  const time = TIME.exec(input.time);
  if (date === null || time === null) return { ok: false, reason: "FORMAT" };
  const [year, month, day] = [Number(date[1]), Number(date[2]), Number(date[3])];
  const [hour, minute] = [Number(time[1]), Number(time[2])];
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(naive);
  if (
    hour > 23 ||
    minute > 59 ||
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return { ok: false, reason: "FORMAT" };
  }
  if (!isKnownZone(input.zone)) return { ok: false, reason: "ZONE_UNKNOWN" };

  // The offset of a zone changes at most a few times a year, so the offsets a day before and a day after the naive time are the only
  // candidates. A candidate is real when the zone really has that offset at the instant it gives.
  const DAY = 24 * 60 * 60 * 1000;
  const candidates = [
    ...new Set([
      offsetMinutesAt(input.zone, naive - DAY),
      offsetMinutesAt(input.zone, naive + DAY),
    ]),
  ];
  const valid = candidates
    .map((offset) => ({ offset, instantMs: naive - offset * 60000 }))
    .filter(({ offset, instantMs }) => offsetMinutesAt(input.zone, instantMs) === offset)
    .sort((a, b) => a.instantMs - b.instantMs);

  if (valid.length === 0) return { ok: false, reason: "NOT_EXISTENT" };
  const [earlier, later] = valid;
  if (earlier !== undefined && later === undefined) {
    return {
      ok: true,
      instant: new Date(earlier.instantMs).toISOString(),
      offsetMinutes: earlier.offset,
      kind: "UNIQUE",
    };
  }
  if (input.choice === undefined) return { ok: false, reason: "AMBIGUOUS" };
  const picked = input.choice === "earlier" ? earlier : later;
  if (picked === undefined) return { ok: false, reason: "AMBIGUOUS" };
  return {
    ok: true,
    instant: new Date(picked.instantMs).toISOString(),
    offsetMinutes: picked.offset,
    kind: input.choice === "earlier" ? "OVERLAP_EARLIER" : "OVERLAP_LATER",
  };
}

const IN_ZONE = new Map<string, Intl.DateTimeFormat>();

/** "18 Oct 2026, 15:30" in the given zone, followed by the zone name. A bad value is a dash. */
export function formatInstantInZone(value: string, zone: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || !isKnownZone(zone)) return "—";
  let format = IN_ZONE.get(zone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    IN_ZONE.set(zone, format);
  }
  return `${format.format(date)} ${zone}`;
}

export const WALL_TIME_MESSAGES: Readonly<Record<WallTimeFailure, string>> = {
  FORMAT: "Enter a date as YYYY-MM-DD and a time as HH:MM (24 hour).",
  ZONE_UNKNOWN: "This is not a time zone name. Use a name like Europe/London.",
  NOT_EXISTENT:
    "This time does not exist in that zone (the clocks skip it). Choose a different time.",
  AMBIGUOUS:
    "This time happens twice in that zone (the clocks go back). Choose the earlier or the later one.",
};
